import { readStoredSignals } from '../data/signals';
import { getSnapshot } from '../data/store';
import { searchAll } from '../logic/desk';
import { isDesktop } from './breakpoint';
import { h, replaceChildren, span } from './dom';
import { icon } from './icons';
import { isTypingEvent } from './keys';
import { STAGE_LABELS } from './labels';
import { navigate } from './router';

/** The sidebar's search (D65): leads from the mirror and new businesses from the stored list, as you
 *  type — offline too. ↑/↓ move, Enter opens, Esc closes; "/" anywhere focuses it. */

interface Hit {
  href: string;
  title: string;
  meta: string;
}

export const searchField = (): HTMLElement => {
  const input = h('input', {
    type: 'search',
    class: 'search__input',
    placeholder: 'Search',
    'aria-label': 'Search leads and new businesses',
    autocomplete: 'off',
    spellcheck: 'false',
  });
  const pop = h('div', { class: 'search__pop', role: 'listbox', hidden: true });
  let hits: Hit[] = [];
  let active = -1;
  let seq = 0;

  const close = () => {
    seq++; // a lookup still reading the mirror must not reopen the popover when it lands
    pop.hidden = true;
    active = -1;
  };
  const paintActive = () =>
    pop.querySelectorAll('.search__hit').forEach((el, i) => {
      el.classList.toggle('search__hit--active', i === active);
      el.setAttribute('aria-selected', String(i === active));
    });
  const open = (hit: Hit) => {
    close();
    input.value = '';
    input.blur();
    navigate(hit.href);
  };
  const hitEl = (hit: Hit, i: number) =>
    h(
      'a',
      {
        class: 'search__hit',
        href: hit.href,
        role: 'option',
        // keep the focus in the field: a blur would close the popover before the click lands
        onMousedown: (e: MouseEvent) => e.preventDefault(),
        onMouseenter: () => {
          active = i;
          paintActive();
        },
        onClick: (e: MouseEvent) => {
          e.preventDefault();
          open(hit);
        },
      },
      span('search__title', hit.title),
      span('search__meta', hit.meta),
    );

  const run = async () => {
    const mine = ++seq;
    const query = input.value;
    if (!query.trim()) return close();
    const [snap, stored] = await Promise.all([getSnapshot(), readStoredSignals()]);
    if (mine !== seq) return;
    const found = searchAll(snap.leads, stored?.list.signals ?? [], query);
    const leads: Hit[] = found.leads.map((l) => ({
      href: `#/leads/${encodeURIComponent(l.id)}`,
      title: l.business_name,
      meta: [l.neighborhood ?? l.address, STAGE_LABELS[l.stage]].filter(Boolean).join(' · '),
    }));
    const businesses: Hit[] = found.businesses.map((s) => ({
      href: `#/signals?id=${encodeURIComponent(s.id)}`,
      title: s.business_name,
      meta: [s.city, 'New business'].filter(Boolean).join(' · '),
    }));
    hits = [...leads, ...businesses];
    active = hits.length ? 0 : -1;
    let i = 0;
    replaceChildren(
      pop,
      hits.length === 0 ? h('div', { class: 'search__empty' }, `No match for “${query.trim()}”`) : null,
      leads.length
        ? [h('div', { class: 'search__group' }, 'Leads'), leads.map((hit) => hitEl(hit, i++))]
        : null,
      businesses.length
        ? [h('div', { class: 'search__group' }, 'New businesses'), businesses.map((hit) => hitEl(hit, i++))]
        : null,
    );
    pop.hidden = false;
    paintActive();
  };

  input.addEventListener('input', () => void run());
  input.addEventListener('blur', close);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      close();
      input.blur();
      return;
    }
    if (pop.hidden || hits.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      active = (active + (e.key === 'ArrowDown' ? 1 : hits.length - 1)) % hits.length;
      paintActive();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = hits[active];
      if (hit) open(hit);
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || isTypingEvent(e) || !isDesktop()) return;
    e.preventDefault();
    input.focus();
  });
  return h('div', { class: 'search' }, icon('search'), input, pop);
};
